// Phase 9.3 — BOM management page.
// Route: /tools/fnb-po/bom

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 50;

const MAPPING_TYPES = [
  { key: "", label: "All types" },
  { key: "direct", label: "Direct" },
  { key: "recipe", label: "Recipe" },
  { key: "unmapped", label: "Unmapped" },
];

function TypePill({ type }) {
  const map = {
    direct: "bg-emerald-50 border-emerald-300 text-emerald-900",
    recipe: "bg-blue-50 border-blue-300 text-blue-900",
    unmapped: "bg-neutral-100 border-neutral-300 text-neutral-700",
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wider ${map[type] || ""}`}>
      {type}
    </span>
  );
}

function VerifyPill({ isVerified, autoSuggested }) {
  if (isVerified) {
    return <span className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-900" data-testid="bom-verified">✓ Verified</span>;
  }
  if (autoSuggested) {
    return <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-900" data-testid="bom-auto-suggested">⟳ Auto-suggested</span>;
  }
  return <span className="text-[10px] text-muted-foreground">—</span>;
}

export default function FnbPoBom() {
  const [summary, setSummary] = useState(null);
  const [mappingType, setMappingType] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("");   // "", "true", "false"
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get("/fnb/bom/summary");
      setSummary(r.data);
    } catch (e) {}
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: PAGE_SIZE, offset });
      if (mappingType) p.set("mapping_type", mappingType);
      if (verifiedFilter) p.set("is_verified", verifiedFilter);
      if (search) p.set("search", search);
      const r = await api.get(`/fnb/bom?${p}`);
      setData(r.data);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setLoading(false);
    }
  }, [mappingType, verifiedFilter, search, offset]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { setOffset(0); }, [mappingType, verifiedFilter, search]);

  const runAutoSuggest = async () => {
    if (!window.confirm("Run auto-mapper across all POS SKUs? Existing verified BOMs are skipped.")) return;
    setAutoLoading(true);
    try {
      const r = await api.post("/fnb/bom/auto-suggest");
      const d = r.data;
      toast.success(
        `Auto-mapper: ${d.tier1_direct} direct SKU + ${d.tier2_name_direct_cat} fuzzy · ${d.unmapped} unmapped · ${d.skipped_verified} skipped`,
      );
      await loadSummary();
      await loadList();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setAutoLoading(false);
    }
  };

  const onBulkUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/fnb/bom/bulk-upload", fd);
      setUploadResult(r.data);
      toast.success(`Upserted ${r.data.upsert_count} BOM(s). ${r.data.errors.length} errors.`);
      await loadSummary();
      await loadList();
    } catch (err) {
      toast.error(detailFromError(err));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const runRecompute = async () => {
    try {
      const r = await api.post("/fnb/analytics/recompute-calculated-levels?days=60");
      const d = r.data;
      toast.success(
        `${d.populated}/${d.total_items} items now have calculated levels · ${d.insufficient_data} insufficient · ${d.no_bom} no-BOM`,
      );
    } catch (e) {
      toast.error(detailFromError(e));
    }
  };

  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <div className="space-y-6" data-testid="fnb-po-bom">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="bom-heading">
            BOM Mapping
          </h1>
          <p className="text-sm text-muted-foreground">
            Link POS-side menu items to stock SKUs. Verified mappings drive
            consumption-based reorder / danger / max levels.
          </p>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/tools/fnb-po">← Dashboard</Link>
        </Button>
      </header>

      {/* Summary chips */}
      <div className="grid gap-3 sm:grid-cols-4">
        <Card data-testid="chip-verified">
          <CardContent className="pt-6">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Verified</div>
            <div className="text-2xl font-semibold" data-testid="chip-verified-count">
              {summary?.verified ?? "—"}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="chip-auto-suggested">
          <CardContent className="pt-6">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Auto-suggested (unverified)</div>
            <div className="text-2xl font-semibold text-amber-900" data-testid="chip-auto-count">
              {summary?.auto_suggested ?? "—"}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="chip-unmapped">
          <CardContent className="pt-6">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Unmapped</div>
            <div className="text-2xl font-semibold" data-testid="chip-unmapped-count">
              {summary?.unmapped ?? "—"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total POS SKUs</div>
            <div className="text-2xl font-semibold" data-testid="chip-total-count">
              {summary?.total ?? "—"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardContent className="pt-6 flex flex-wrap items-center gap-3">
          <Button
            onClick={runAutoSuggest} disabled={autoLoading}
            data-testid="auto-suggest-btn"
          >
            {autoLoading ? "Running auto-mapper…" : "Auto-suggest matches"}
          </Button>
          <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border/70 text-sm font-medium hover:bg-muted transition-colors cursor-pointer" data-testid="bulk-upload-label">
            <input
              ref={fileInputRef}
              type="file" accept=".csv"
              className="hidden"
              onChange={onBulkUpload}
              data-testid="bulk-upload-input"
            />
            Bulk upload BOM CSV
          </label>
          <a
            href={`${API}/fnb/bom/bulk-upload/template`}
            className="text-xs text-primary underline underline-offset-4"
            data-testid="download-template"
          >
            Download template →
          </a>
          <Button
            variant="outline" size="sm"
            onClick={runRecompute}
            data-testid="recompute-btn"
          >
            Recompute calculated levels
          </Button>
        </CardContent>
      </Card>

      {uploadResult && (
        <Card className="border-blue-300" data-testid="upload-result-card">
          <CardContent className="pt-6 text-xs">
            <div className="font-medium mb-1">Upload result</div>
            <div>Upserted: <b>{uploadResult.upsert_count}</b> BOM(s)</div>
            {uploadResult.errors.length > 0 && (
              <div className="mt-2 space-y-0.5">
                <div className="text-destructive">Errors:</div>
                {uploadResult.errors.map((e, i) => (
                  <div key={i} className="pl-2">Row {e.row}: {e.error}</div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filter row */}
      <Card>
        <CardContent className="pt-6 flex flex-wrap items-center gap-3">
          <Select value={mappingType} onValueChange={(v) => setMappingType(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-40 text-xs" data-testid="filter-type">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              {MAPPING_TYPES.map((t) => (
                <SelectItem key={t.key || "all"} value={t.key || "__all__"} className="text-xs">
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={verifiedFilter} onValueChange={(v) => setVerifiedFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-40 text-xs" data-testid="filter-verified">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__" className="text-xs">All statuses</SelectItem>
              <SelectItem value="true" className="text-xs">Verified only</SelectItem>
              <SelectItem value="false" className="text-xs">Unverified only</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Search POS SKU / name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs text-xs"
            data-testid="bom-search"
          />
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between text-sm">
            <div><b>{total}</b> BOM row{total === 1 ? "" : "s"}</div>
            {loading && <div className="text-xs text-muted-foreground">Loading…</div>}
          </div>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center" data-testid="bom-empty">
              No BOM rows yet. Run <b>Auto-suggest matches</b> above to seed from sales data.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="bom-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>POS SKU</TableHead>
                    <TableHead>POS item name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Ingredients</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => (
                    <TableRow key={r.pos_sku} data-testid={`bom-row-${r.pos_sku}`}>
                      <TableCell className="font-mono text-xs">{r.pos_sku}</TableCell>
                      <TableCell className="text-sm max-w-md">{r.pos_item_name}</TableCell>
                      <TableCell><TypePill type={r.mapping_type} /></TableCell>
                      <TableCell className="text-right text-sm">
                        {(r.ingredients || []).length}
                      </TableCell>
                      <TableCell>
                        <VerifyPill
                          isVerified={r.is_verified}
                          autoSuggested={r.auto_suggested}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Link
                          to={`/tools/fnb-po/bom/${encodeURIComponent(r.pos_sku)}`}
                          className="text-xs text-primary underline underline-offset-4"
                          data-testid={`bom-edit-${r.pos_sku}`}
                        >
                          Open →
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between pt-4 text-xs">
                <div className="text-muted-foreground">
                  Page {Math.floor(offset / PAGE_SIZE) + 1} of{" "}
                  {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    data-testid="bom-prev"
                  >← Prev</Button>
                  <Button
                    variant="outline" size="sm"
                    disabled={offset + items.length >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    data-testid="bom-next"
                  >Next →</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
